























































































































































































































































































































































































































































import Vue from "vue";
import { mapActions, mapGetters, mapState } from "vuex";
import { has } from "lodash";
import iwStatusToggle from "@/shared/components/StatusToggle.vue";
import iwDropdown from "@/shared/components/Dropdown.vue";
import datetimeMixin from "@/shared/mixins/datetimeAttribute";
import { daysBetween } from "@/shared/scripts/helpers/date";
import { Trans } from "@/shared/scripts/helpers/translation/translation";
import { duplicateClassified, toggleUnderOptionStatus } from "@/shared/scripts/ClassifiedsApi";
import { Classified } from "@/shared/interfaces/classified/Classified.interface";
import { i18n } from "@/shared/scripts/helpers/translation/i18n";
import { toOptimizedPictureUrl } from "@/shared/scripts/mediaResize";
import { TranslateResult } from "vue-i18n";
import { RawLocation } from "vue-router";

interface Flag {
    name: string;
    color: string;
}

export default Vue.extend({
    name: "ClassifiedsListItem",
    components: {
        iwStatusToggle: iwStatusToggle,
        iwDropdown: iwDropdown
    },
    mixins: [datetimeMixin],
    props: {
        classifiedData: {
            type: Object as () => Classified,
            required: true
        }
    },
    computed: {
        ...(mapState("Time", ["now"]) as { now: () => Date }),
        ...mapGetters({
            isMasquerading: "UserModule/isMasquerading"
        }),

        firstPictureUrl: function(): string | null {
            if (
                this.classifiedData.pictures &&
                this.classifiedData.pictures.length > 0
            ) {
                return toOptimizedPictureUrl(this.classifiedData.pictures[0].url);
            }
            return null;
        },

        title: function(): string {
            if (this.classifiedData.property?.subtype) {
                return this.$t(this.classifiedData.property.subtype.toLowerCase()) as string;
            } else if (this.classifiedData.property?.type) {
                return this.$t(this.classifiedData.property.type.toLowerCase()) as string;
            }
            return "";
        },

        transaction: function(): string {
            if (this.classifiedData.transaction?.type?.toLowerCase() === "for_sale") {
                return this.$t("to_buy") as string;
            }
            return this.$t("to_rent") as string;
        },
        flags: function(): Flag[] {
            const flagsList: Flag[] = [];
            if (this.classifiedData.transaction?.isUnderOption) {
                flagsList.push({
                    name: this.$t("under_option") as string,
                    color: "primary"
                });
            }
            if (
                this.classifiedData.transaction?.type === "FOR_SALE" &&
                this.classifiedData.transaction?.soldOrRented?.isSoldOrRented
            ) {
                flagsList.push({
                    name: this.$t("sold") as string,
                    color: "primary"
                });
            }
            if (
                this.classifiedData.transaction?.type === "FOR_RENT" &&
                this.classifiedData.transaction?.soldOrRented?.isSoldOrRented
            ) {
                flagsList.push({
                    name: this.$t("rented") as string,
                    color: "primary"
                });
            }
            return flagsList;
        },

        address: function(): string {
            const location = this.classifiedData.property?.location?.address;
            if (location) {
                const street = location.street || "";
                const number = location.number || "";
                const postalCode = location.postalCode || "";
                const locality = location.locality || "";
                const streetPart = `${street} ${number}`.trim();
                const cityPart = `${postalCode} ${locality}`.trim();
                if (streetPart && cityPart) {
                    return `${streetPart} - ${cityPart}`;
                }
                return streetPart || cityPart;
            }
            return "";
        },
        price: function(): string | TranslateResult {
            let priceValue: number | TranslateResult = this.$t("no_price");
            if (
                has(this.classifiedData, "transaction.type") &&
                this.classifiedData.transaction.type === "FOR_SALE" &&
                has(this.classifiedData, "transaction.sale.price")
            ) {
                priceValue = this.classifiedData.transaction.sale.price;
            }
            if (
                has(this.classifiedData, "transaction.type") &&
                this.classifiedData.transaction.type === "FOR_RENT" &&
                has(this.classifiedData, "transaction.rental.monthlyRentalPrice")
            ) {
                priceValue = this.classifiedData.transaction.rental.monthlyRentalPrice;
            }
            return typeof priceValue === "number" ? this.formatCurrency(priceValue.toString()) : priceValue;
        },
        bedroomCount: function(): number | null {
            return this.classifiedData.property?.bedroom?.count || null;
        },
        surface: function(): number | null {
            return this.classifiedData.property?.livingDescription?.netHabitableSurface || null;
        },
        expirationDate: function(): Date | null {
            const date = this.classifiedData?.expirationDate;
            if (date) {
                return new Date(date);
            }
            return null;
        },
        expireInDays: function(): number | null {
            if (this.expirationDate) {
                return daysBetween(this.now, this.expirationDate);
            }
            return null;
        },
        formattedExpirationDate: function(): string | null {
            if (!this.expirationDate) {
                return null;
            }
            return this.expirationDate.toLocaleDateString(this.currentLanguage, {
                day: "numeric",
                month: "long",
                year: "numeric"
            });
        },
        showExpirationWarning: function(): boolean {
            return this.classifiedData.status === "ACTIVE" &&
                this.expireInDays !== null &&
                this.expireInDays <= 10;
        },
        expirationLabel: function(): string | null {
            if (!this.formattedExpirationDate) {
                return null;
            }
            if (this.classifiedData.status === "ACTIVE") {
                return `${this.$t("active_until")} ${this.formattedExpirationDate}`;
            }
            if (this.classifiedData.status === "EXPIRED") {
                return `${this.$t("expired_on")} ${this.formattedExpirationDate}`;
            }
            return null;
        },
        showExtendLink: function(): boolean {
            return this.canExtend &&
                (this.classifiedData.status === "ACTIVE" || this.classifiedData.status === "EXPIRED");
        },
        showRenewLink: function(): boolean {
            return this.canRenew && this.classifiedData.status === "EXPIRED";
        },
        visualizationOption: function(): TranslateResult | null {
            if (has(this.classifiedData, "publication.visualizationOption")) {
                const translation = this.classifiedData.publication.visualizationOption as string;
                return this.$t(translation.toLowerCase());
            }
            return null;
        },
        statusLabel: function(): string {
            switch (this.classifiedData.status) {
            case "ACTIVE":
                return this.$t("status_active") as string;
            case "INACTIVE":
                return this.$t("status_inactive") as string;
            case "DRAFT":
                return this.$t("status_draft_badge") as string;
            case "EXPIRED":
                return this.$t("status_expired") as string;
            case "PUBLISHABLE":
                return this.$t("waiting_payment") as string;
            default:
                return this.classifiedData.status;
            }
        },
        statusBadgeClass: function(): string {
            const base = "classifieds-list-item__visibility-badge--";
            switch (this.classifiedData.status) {
            case "ACTIVE":
                return base + "active";
            case "INACTIVE":
                return base + "inactive";
            case "DRAFT":
                return base + "draft";
            case "EXPIRED":
                return base + "expired";
            case "PUBLISHABLE":
                return base + "publishable";
            default:
                return base + "inactive";
            }
        },
        canToggleStatus: function(): boolean {
            return this.classifiedData.status === "ACTIVE";
        },
        activationTooltip: function(): string {
            switch (this.classifiedData.status) {
            case "DRAFT":
                return this.$t("complete_classified") as string;
            case "PUBLISHABLE":
                return this.isMasquerading
                    ? this.$t("activate") as string
                    : this.$t("proceed_to_payment") as string;
            case "INACTIVE":
                return this.$t("classified_reactivate") as string;
            case "EXPIRED":
                if (this.canExtend || this.canRenew) {
                    return this.$t("renew_my_classified") as string;
                }
                return "";
            default:
                return "";
            }
        },
        isToggleActionable: function(): boolean {
            return this.activationTooltip !== "";
        },
        toggleAriaLabel: function(): string {
            if (this.classifiedData.status === "ACTIVE") {
                return this.$t("deactivate") as string;
            }
            if (this.isToggleActionable) {
                return this.activationTooltip;
            }
            return this.$t("status") as string;
        },
        /**
         * A classified can only be upgraded until 10 days before its
         * expiration date.
         *
         * While masquerading, upgrading is always available as long as the
         * classified is not already PREMIUM: back-office agents must be able
         * to upsell during the last 10 days too, and even when no expiration
         * date is known. (IO-138)
         */
        canUpgrade: function(): boolean {
            if (
                this.classifiedData.status !== "ACTIVE" ||
                this.classifiedData.publication?.visualizationOption === "PREMIUM"
            ) {
                return false;
            }
            if (this.isMasquerading) {
                return true;
            }
            if (!this.expirationDate) {
                return false;
            }
            return daysBetween(this.now, this.expirationDate, false) > 10;
        },
        canExtend: function(): boolean {
            if (!this.expirationDate) {
                return false;
            }
            if (this.isMasquerading) {
                return true;
            }
            const diffInDays = daysBetween(this.now, this.expirationDate, false);
            const isWithinTenDaysBefore = diffInDays <= 10;
            const isWithinFiveMonths = diffInDays >= -30 * 5;
            return isWithinTenDaysBefore && isWithinFiveMonths;
        },
        canRenew: function(): boolean {
            if (!this.expirationDate) {
                return false;
            }
            const renewDate = new Date(this.expirationDate.getTime());
            renewDate.setMonth(renewDate.getMonth() + 5);
            return this.now > renewDate;
        },
        packageUpgradeRoute: function(): RawLocation {
            return {
                name: "visibility-upgrade-choice",
                params: { id: this.classifiedData.id }
            };
        },
        currentLanguage: function(): string {
            return Trans.currentLocale;
        }
    },
    methods: {
        ...mapActions({
            setModalTitle: "ModalModule/setModalTitle",
            openMasqueradeModal: "ModalModule/openMasqueradeModal",
            resetValidatedSteps: "StepsWizard/reset"
        }),
        /**
         * All-numeric dates are always rendered day-first: this is a Belgian
         * audience, and plain "en" would format 7 March as 03/07/2026, which
         * reads as 3 July here. Pinning the region keeps dd/mm/yyyy in the
         * three locales. (IO-138)
         */
        formatDate: function(dateString: string): string {
            const dateObject = new Date(dateString);
            return dateObject.toLocaleDateString(`${this.currentLanguage}-BE`, {
                day: "2-digit",
                month: "2-digit",
                year: "numeric"
            });
        },
        handleToggleStatus: function(value: boolean): void {
            if (!value && this.classifiedData.status === "ACTIVE") {
                this.openDeactivateModal();
            }
        },
        handleToggleActivate: function(): void {
            switch (this.classifiedData.status) {
            case "DRAFT":
                this.editClassified("step-property-type");
                break;
            case "PUBLISHABLE":
                if (this.isMasquerading) {
                    this.activateClassified();
                } else {
                    this.goToPayment();
                }
                break;
            case "INACTIVE":
                this.reactivateClassified();
                break;
            case "EXPIRED":
                if (this.canExtend) {
                    this.extendClassified();
                } else if (this.canRenew) {
                    this.renewClassified();
                }
                break;
            }
        },
        goToPayment: function(): void {
            this.editClassified("step-pricing", "?payment=true");
        },
        editClassified: function(stepToSet: string, query = ""): void {
            this.resetValidatedSteps(["step-property-type"]);
            if (this.$store.getters["MyClassifiedsModule/getClassifiedById"](this.classifiedData.id)) {
                const urlTranslated = this.$t(stepToSet) as string;
                const to = this.$router.resolve({ path: urlTranslated + this.classifiedData.id + query });
                this.$router.push(to.location);
            }
            this.closeDropdown();
        },
        openDeactivateModal: function(): void {
            this.$store.commit("ModalModule/openDeactivateModal");
            this.$store.commit("MyClassifiedsModule/setClassifiedToDeactivate", this.classifiedData);
            this.closeDropdown();
        },
        openDeleteModal: function(): void {
            this.$store.commit("ModalModule/openDeleteModal");
            this.$store.commit("MyClassifiedsModule/setClassifiedToDelete", this.classifiedData);
            this.closeDropdown();
        },
        reactivateClassified: function(): void {
            this.$store.commit("MyClassifiedsModule/setClassifiedToReactivate", this.classifiedData);
            if (this.isMasquerading) {
                this.setModalTitle("classified_reactivate");
                this.openMasqueradeModal();
            } else {
                this.$store.commit("ModalModule/openReactivateModal");
            }
            this.closeDropdown();
        },
        extendClassified: function(): void {
            this.$store.commit("MyClassifiedsModule/setClassifiedToExtend", this.classifiedData);
            if (this.isMasquerading) {
                this.setModalTitle("classified_extend");
                this.openMasqueradeModal();
            } else {
                this.$router.push({ name: "extend-choice", params: { id: this.classifiedData.id } });
            }
            this.closeDropdown();
        },
        renewClassified: function(): void {
            duplicateClassified(this.classifiedData)
                .then((newClassified: Classified | boolean) => {
                    if (typeof newClassified === "boolean") {
                        return false;
                    }
                    if (this.isMasquerading) {
                        this.$store.commit("MyClassifiedsModule/unshiftClassified", newClassified);
                    } else {
                        const urlTranslated = this.$t("step-property-type") as string;
                        const to = this.$router.resolve({ path: urlTranslated + newClassified.id });
                        this.$router.push(to.location);
                    }
                })
                .catch((error: Error) => {
                    this.dialog.alert(i18n.t("server_not_responding_error").toString());
                    console.error(error);
                });
            this.closeDropdown();
        },
        /**
         * Masquerade-only: publish an unpaid (PUBLISHABLE) classified on the
         * customer's behalf, without sending them through payment. (IO-138)
         *
         * The store commit must happen BEFORE the modal is opened:
         * MasqueradeActivate's created() hook reads classifiedToActivate
         * straight away, and getPricing() throws synchronously on an empty
         * classified.
         */
        activateClassified: function(): void {
            this.$store.commit("MyClassifiedsModule/setClassifiedToActivate", this.classifiedData);
            this.setModalTitle("classified_activate");
            this.openMasqueradeModal();
            this.closeDropdown();
        },
        upgrade: function(): void {
            this.$store.commit("MyClassifiedsModule/setClassifiedToUpgrade", this.classifiedData);
            if (this.isMasquerading) {
                this.setModalTitle("classified_upgrade");
                this.openMasqueradeModal();
            } else {
                this.$router.push(this.packageUpgradeRoute);
            }
            this.closeDropdown();
        },
        toggleOptionStatus: function(): void {
            toggleUnderOptionStatus(this.classifiedData)
                .then((res: boolean) => {
                    const updatedClassified = {
                        ...this.classifiedData,
                        transaction: {
                            ...this.classifiedData.transaction,
                            isUnderOption: res
                        }
                    };
                    this.$store.commit("MyClassifiedsModule/replaceClassified", updatedClassified);
                })
                .catch((e: Error) => {
                    this.dialog.alert(i18n.t("server_not_responding_error").toString());
                    console.error(e);
                });
            this.closeDropdown();
        },
        seeImmowebPage: function(): void {
            window.location.href = process.env.VUE_APP_IMMOWEB_URL +
                `/${this.currentLanguage}` +
                `/${this.$t("classified")}/${this.classifiedData.legacyId}`;
        },
        closeDropdown: function(): void {
            const dropdown = this.$refs.actionsDropdown as Vue & { closeList: () => void };
            if (dropdown) {
                dropdown.closeList();
            }
        }
    }
});
